import React, { useMemo, useState } from 'react';
import { Swords, Shield, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { getMatchupAnalytics } from '../../lib/analytics';
import { getChampionIconUrl } from '../../lib/champions';
import { SectionHeader, Card, Insight, EmptyState } from './shared';

/**
 * APEX v8.21 — MatchupsSection
 *
 * Affichage role-aware des matchups :
 *   - Main enemies : champion adverse principal avec synergies expandables
 *   - Allies 1 & 2 : alliés/ennemis clés selon le rôle
 *
 * Structure par rôle :
 *   ADC     : ADC adverse + synergies support | supports alliés | junglers alliés
 *   Mid     : mid adverse + synergies jungler | junglers alliés | junglers ennemis
 *   Top     : top adverse + synergies jungler | junglers alliés | junglers ennemis
 *   Jungle  : jungler adverse + synergies sup | supports alliés | supports ennemis
 *   Support : support adverse + synergies ADC | ADCs alliés     | junglers alliés
 */

// ── ChampIcon ──────────────────────────────────────────────────────────────
const ChampIcon = ({ name, champions, version, size = 28 }) => {
  const champ = champions?.find(c =>
    c.name?.toLowerCase() === name?.toLowerCase() ||
    c.id?.toLowerCase()   === name?.toLowerCase()
  );
  if (!champ) return (
    <div style={{ width: size, height: size }}
      className="bg-zinc-800 rounded-sm shrink-0 flex items-center justify-center">
      <span className="text-[8px] text-zinc-600 font-mono truncate px-0.5">
        {(name || '?').slice(0, 2)}
      </span>
    </div>
  );
  return (
    <img
      src={getChampionIconUrl(champ.id, version)}
      alt={champ.name}
      style={{ width: size, height: size }}
      className="object-cover rounded-sm shrink-0"
      loading="lazy"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  );
};

// ── WR Badge ──────────────────────────────────────────────────────────────
const WrBadge = ({ wr }) => {
  const color = wr >= 60 ? 'text-cyan-400' : wr >= 45 ? 'text-amber-400' : 'text-rose-400';
  return <span className={`font-mono text-sm font-bold ${color}`}>{wr}%</span>;
};

// ── MatchupRow : champion adverse avec synergies expandables ───────────────
const MatchupRow = ({ entry, champions, version }) => {
  const [open, setOpen] = useState(false);
  const hasSynergies = entry.synergies?.length > 0;

  return (
    <div className="border-b border-zinc-900 last:border-0">
      {/* Ligne principale */}
      <div
        className={`flex items-center gap-3 py-2 px-2 transition hover:bg-zinc-900/40 ${hasSynergies ? 'cursor-pointer' : ''}`}
        onClick={() => hasSynergies && setOpen(o => !o)}
      >
        <ChampIcon name={entry.name} champions={champions} version={version} size={30} />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-sm text-zinc-200 truncate">{entry.name}</div>
          <div className="text-[10px] text-zinc-600 font-mono">{entry.games} game{entry.games > 1 ? 's' : ''}</div>
        </div>
        <WrBadge wr={entry.winrate} />
        {hasSynergies && (
          <div className="text-zinc-600 ml-1">
            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </div>
        )}
      </div>

      {/* Synergies expandées */}
      {open && hasSynergies && (
        <div className="bg-zinc-900/30 px-3 pb-2 space-y-1">
          {entry.synergies.map(s => (
            <div key={s.name} className="flex items-center gap-2 py-1">
              <ChampIcon name={s.name} champions={champions} version={version} size={20} />
              <div className="flex-1 text-xs text-zinc-400 font-mono truncate">{s.name}</div>
              <div className="text-[10px] text-zinc-600 font-mono mr-2">{s.games}G</div>
              <WrBadge wr={s.winrate} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── AllyRow : champion allié/ennemi simple ─────────────────────────────────
const AllyRow = ({ entry, champions, version }) => (
  <div className="flex items-center gap-3 py-2 px-2 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/40 transition">
    <ChampIcon name={entry.name} champions={champions} version={version} size={28} />
    <div className="flex-1 min-w-0">
      <div className="font-mono text-sm text-zinc-200 truncate">{entry.name}</div>
      <div className="text-[10px] text-zinc-600 font-mono">{entry.games} game{entry.games > 1 ? 's' : ''}</div>
    </div>
    <WrBadge wr={entry.winrate} />
  </div>
);

// ── ChampionList : liste avec "voir plus" ──────────────────────────────────
const ChampionList = ({ data, type = 'enemy', champions, version, minGames = 1 }) => {
  const [expanded, setExpanded] = useState(false);
  const MAX = 10;

  const filtered = data.filter(d => d.games >= minGames);
  const visible  = expanded ? filtered : filtered.slice(0, MAX);
  const surplus  = filtered.length - MAX;

  if (filtered.length === 0) {
    return (
      <div className="text-xs text-zinc-600 font-mono text-center py-4">
        Pas assez de données (min {minGames} game{minGames > 1 ? 's' : ''})
      </div>
    );
  }

  const Row = type === 'enemy' ? MatchupRow : AllyRow;

  return (
    <div>
      {visible.map(entry => (
        <Row key={entry.name} entry={entry} champions={champions} version={version} />
      ))}
      {surplus > 0 && (
        <button
          onClick={() => setExpanded(e => !e)}
          className="w-full text-center text-[10px] tracking-wider text-zinc-500 hover:text-cyan-400 py-2 transition border-t border-zinc-900 flex items-center justify-center gap-1"
        >
          {expanded
            ? <><ChevronUp size={11} /> Réduire</>
            : <><ChevronDown size={11} /> Voir {surplus} de plus</>
          }
        </button>
      )}
    </div>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────
export default function MatchupsSection({ games, champions, version, userSettings }) {
  const [minGames, setMinGames] = useState(1);

  const role = (userSettings?.primary_role || 'ADC').toUpperCase();

  const data = useMemo(
    () => getMatchupAnalytics(games, role),
    [games, role]
  );

  // ── Insights auto ────────────────────────────────────────────────────────
  const bestEnemy  = data.mainEnemies.filter(e => e.games >= 3).sort((a, b) => b.winrate - a.winrate)[0];
  const worstEnemy = data.mainEnemies.filter(e => e.games >= 3).sort((a, b) => a.winrate - b.winrate)[0];
  const bestAlly1  = data.allies1.filter(a => a.games >= 3).sort((a, b) => b.winrate - a.winrate)[0];

  return (
    <div className="space-y-6">

      {/* Filtre min games */}
      <div className="flex items-center gap-3">
        <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-semibold">Min games</span>
        {[1, 2, 3, 5].map(n => (
          <button
            key={n}
            onClick={() => setMinGames(n)}
            className={`px-3 py-1 text-xs font-mono transition border ${minGames === n ? 'bg-cyan-400 text-black border-cyan-400' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}
          >
            {n}+
          </button>
        ))}
      </div>

      {/* Insights */}
      {(bestEnemy || worstEnemy || bestAlly1) && (
        <div className="space-y-2">
          {bestAlly1 && bestAlly1.games >= 3 && (
            <Insight variant="success">
              <strong>Meilleur allié :</strong> {bestAlly1.name} — {bestAlly1.winrate}% WR sur {bestAlly1.games} games.
            </Insight>
          )}
          {worstEnemy && worstEnemy.winrate < 40 && (
            <Insight variant="danger">
              <strong>Matchup difficile :</strong> vs {worstEnemy.name} — {worstEnemy.winrate}% WR sur {worstEnemy.games} games.
            </Insight>
          )}
          {bestEnemy && bestEnemy.winrate > 65 && bestEnemy !== worstEnemy && (
            <Insight variant="info">
              <strong>Matchup favorable :</strong> vs {bestEnemy.name} — {bestEnemy.winrate}% WR sur {bestEnemy.games} games.
            </Insight>
          )}
        </div>
      )}

      {/* Grid 3 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Main enemies */}
        <Card>
          <SectionHeader title={data.labels.main} subtitle="Clic pour voir les synergies" icon={Swords} />
          <ChampionList
            data={data.mainEnemies}
            type="enemy"
            champions={champions}
            version={version}
            minGames={minGames}
          />
        </Card>

        {/* Allies 1 */}
        <Card>
          <SectionHeader title={data.labels.ally1} subtitle="Impact sur ton WR" icon={Shield} />
          <ChampionList
            data={data.allies1}
            type="ally"
            champions={champions}
            version={version}
            minGames={minGames}
          />
        </Card>

        {/* Allies 2 */}
        <Card>
          <SectionHeader title={data.labels.ally2} subtitle="Impact sur ton WR" icon={Users} />
          <ChampionList
            data={data.allies2}
            type="ally"
            champions={champions}
            version={version}
            minGames={minGames}
          />
        </Card>

      </div>
    </div>
  );
}
